// CAPABILITY: Billing modal — two tiers (Standard / Pro) with a monthly|annual
// toggle that shows explicit annual savings. Existing subscribers switch plans
// in place via changeSubscription; new customers go through hosted Checkout.
import { CheckIcon } from "@heroicons/react/20/solid";
import { addDoc, collection, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import React, { useState } from "react";
import {
  STRIPE_ANNUAL_CHAT_PRICE_ID,
  STRIPE_ANNUAL_PRICE_ID,
  STRIPE_MONTHLY_CHAT_PRICE_ID,
  STRIPE_MONTHLY_PRICE_ID,
  STRIPE_PORTAL_ID,
  isChatEnabledPriceId,
} from "../../../constants";
import { firestore, functions } from "../../../firebase";
import { useAuth } from "../../contexts/AuthContext";

// Two-tier plan model. Amounts mirror the Stripe prices in constants.js.
const TIERS = [
  {
    key: "base",
    name: "Standard",
    chat: false,
    tagline: "Everything you need to publish branded video pages.",
    monthly: { id: STRIPE_MONTHLY_PRICE_ID, amount: 4.99 },
    annual: { id: STRIPE_ANNUAL_PRICE_ID, amount: 49.99 },
    features: [
      "Unlimited published pages",
      "Custom branding & logo",
      "Password protection",
      "Scheduled countdowns",
      "Viewer analytics & CSV export",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    chat: true,
    featured: true,
    tagline: "Standard, plus live viewer chat with AI moderation.",
    monthly: { id: STRIPE_MONTHLY_CHAT_PRICE_ID, amount: 15 },
    annual: { id: STRIPE_ANNUAL_CHAT_PRICE_ID, amount: 120 },
    features: [
      "Everything in Standard",
      "Live viewer chat",
      "AI moderation — Low / Medium / High",
      "Context-aware filtering",
      "Persistent viewer names",
    ],
  },
];

function savingsFor(tier) {
  const monthlyTotal = tier.monthly.amount * 12;
  const saved = monthlyTotal - tier.annual.amount;
  return {
    perMonth: tier.annual.amount / 12,
    saved,
    monthsFree: Math.round(saved / tier.monthly.amount),
    pct: Math.round((saved / monthlyTotal) * 100),
  };
}

function PlanCard({ tier, period, isCurrent, isSubscribed, disabled, onSelect }) {
  const isAnnual = period === "annual";
  const active = isAnnual ? tier.annual : tier.monthly;
  const { perMonth, saved, monthsFree } = savingsFor(tier);
  const shown = isAnnual ? perMonth : tier.monthly.amount;

  const ring = isCurrent
    ? "border-emerald-500 ring-1 ring-emerald-500"
    : tier.featured
    ? "border-[#FF3A20] ring-1 ring-[#FF3A20]"
    : "border-slate-200";

  const actionLabel = isCurrent
    ? "Current plan"
    : isSubscribed
    ? "Switch to this plan"
    : `Choose ${tier.name}`;

  return (
    <div className={`relative flex flex-col rounded-2xl border bg-white p-6 ${ring}`}>
      {tier.featured && !isCurrent && (
        <span className="absolute -top-3 left-6 rounded-full bg-[#FF3A20] px-3 py-1 text-xs font-semibold text-white shadow-sm">
          Most popular
        </span>
      )}
      {isCurrent && (
        <span className="absolute -top-3 left-6 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
          Current plan
        </span>
      )}

      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold text-slate-950">{tier.name}</h3>
        {tier.chat && (
          <span className="status-pill bg-indigo-50 text-indigo-700 ring-indigo-200">Chat</span>
        )}
      </div>
      <p className="mt-1 text-sm leading-6 text-slate-500">{tier.tagline}</p>

      <div className="mt-5 flex items-end gap-1 text-slate-950">
        <span className="pb-2 text-xl font-semibold">$</span>
        <span className="text-5xl font-bold tracking-tight">{shown.toFixed(2)}</span>
        <span className="pb-2 text-sm font-medium text-slate-500">/mo</span>
      </div>
      {/* Fixed-height block so switching Monthly/Annual doesn't shift the card. */}
      <div className="mt-1 min-h-[3.25rem]">
        <p className="text-sm text-slate-500">
          {isAnnual ? `Billed $${tier.annual.amount.toFixed(2)}/year` : "Billed monthly · cancel anytime"}
        </p>
        {isAnnual && (
          <span className="mt-2 inline-flex w-fit items-center gap-1 whitespace-nowrap rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
            Save ${saved.toFixed(0)} · {monthsFree} months free
          </span>
        )}
      </div>

      <ul className="mt-5 flex-1 space-y-2.5">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
            <CheckIcon className={`mt-0.5 h-4 w-4 flex-none ${tier.featured ? "text-[#FF3A20]" : "text-emerald-600"}`} />
            {f}
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={() => onSelect(active.id, tier.chat)}
        disabled={disabled || isCurrent}
        className={
          isCurrent
            ? "secondary-button mt-6 w-full cursor-default"
            : tier.featured
            ? "primary-button mt-6 w-full"
            : "secondary-button mt-6 w-full"
        }
      >
        {actionLabel}
      </button>
    </div>
  );
}

export default function BillingModal({ isOpen, onClose }) {
  const { currentUser, isSubscribed, activePriceId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [period, setPeriod] = useState(() =>
    [STRIPE_MONTHLY_PRICE_ID, STRIPE_MONTHLY_CHAT_PRICE_ID].includes(activePriceId)
      ? "monthly"
      : "annual"
  );

  if (!isOpen) return null;

  const chatPlansConfigured =
    isChatEnabledPriceId(STRIPE_MONTHLY_CHAT_PRICE_ID) &&
    isChatEnabledPriceId(STRIPE_ANNUAL_CHAT_PRICE_ID);

  const handleBillingPortal = async () => {
    setLoading(true);
    setError("");
    try {
      const functionRef = httpsCallable(functions, "ext-firestore-stripe-payments-createPortalLink");
      const { data } = await functionRef({
        returnUrl: window.location.origin,
        locale: "auto",
        configuration: STRIPE_PORTAL_ID,
      });
      window.location.assign(data.url);
    } catch (portalError) {
      setError(portalError.message);
      setLoading(false);
    }
  };

  const checkoutWithPrice = async (priceId) => {
    console.info(`STRIPE_CHECKOUT_START price=${priceId} uid=${currentUser?.uid || "none"}`);
    let unsubscribe = () => {};

    const watchdog = setTimeout(() => {
      unsubscribe();
      setLoading(false);
      setError(
        "Stripe didn't respond. Check that this price is ACTIVE and in the same mode " +
          "(Test vs Live) as the firestore-stripe-payments extension, then try again."
      );
      console.warn(`STRIPE_CHECKOUT_TIMEOUT price=${priceId}`);
    }, 15000);

    try {
      const checkoutSessionsRef = collection(firestore, "customers", currentUser.uid, "checkout_sessions");
      const docRef = await addDoc(checkoutSessionsRef, {
        price: priceId,
        success_url: window.location.origin,
        cancel_url: window.location.origin,
      });
      console.info(`STRIPE_CHECKOUT_SESSION_DOC_CREATED doc=${docRef.id}`);

      unsubscribe = onSnapshot(
        docRef,
        (snap) => {
          const data = snap.data();
          if (!data) return;
          const { error: sessionError, url } = data;
          if (sessionError) {
            clearTimeout(watchdog);
            unsubscribe();
            setError(`Stripe: ${sessionError.message}`);
            setLoading(false);
            console.warn(`STRIPE_CHECKOUT_SESSION_ERROR price=${priceId} message=${sessionError.message}`);
          }
          if (url) {
            clearTimeout(watchdog);
            unsubscribe();
            console.info(`STRIPE_CHECKOUT_REDIRECT price=${priceId}`);
            window.location.assign(url);
          }
        },
        (snapError) => {
          clearTimeout(watchdog);
          setError(snapError.message);
          setLoading(false);
          console.warn(`STRIPE_CHECKOUT_SNAPSHOT_FAILED price=${priceId} message=${snapError.message}`);
        }
      );
    } catch (writeError) {
      clearTimeout(watchdog);
      setError(writeError.message);
      setLoading(false);
      console.warn(`STRIPE_CHECKOUT_WRITE_FAILED price=${priceId} message=${writeError.message}`);
    }
  };

  // Existing subscriber -> swap plan in place (prorated, dedup). New -> Checkout.
  const selectPlan = async (priceId, isChatPlan) => {
    if (isChatPlan && !chatPlansConfigured) {
      setError("Chat plans are not configured yet. Please try again later.");
      return;
    }
    setError("");
    setSuccess("");

    if (!isSubscribed) {
      setLoading(true);
      await checkoutWithPrice(priceId);
      return;
    }

    setLoading(true);
    try {
      const changeRef = httpsCallable(functions, "changeSubscription");
      const { data } = await changeRef({ priceId });
      console.info(
        `PLAN_CHANGE_OK sub=${data?.subscriptionId || "?"} canceled_duplicates=${data?.canceledDuplicates ?? "?"}`
      );
      setLoading(false);
      setSuccess(
        data?.canceledDuplicates
          ? `Plan updated. Removed ${data.canceledDuplicates} duplicate subscription(s).`
          : "Plan updated. Your subscription has been switched."
      );
    } catch (changeError) {
      const message = changeError?.message || "";
      console.warn(`PLAN_CHANGE_FAILED code=${changeError?.code || "unknown"} message=${message}`);
      if (/no_active_subscription/.test(message)) {
        await checkoutWithPrice(priceId);
      } else {
        setLoading(false);
        setError(message || "Could not change plan.");
      }
    }
  };

  const seg = (on) =>
    `relative flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition ${
      on ? "bg-white text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-700"
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
      <button
        type="button"
        className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close billing modal"
      />

      <div className="relative z-10 max-h-[calc(100dvh-48px)] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-5 py-6 text-center sm:px-7">
          <div className="flex items-start justify-between gap-5 text-left">
            <div>
              <p className="section-eyebrow">Plans</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
                {isSubscribed ? "Manage your plan" : "Choose your plan"}
              </h2>
              <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">
                Publish unlimited branded pages. Upgrade to Pro for live viewer chat with
                AI moderation. Switch or cancel anytime.
              </p>
            </div>
            <button type="button" className="secondary-button px-3" onClick={onClose}>
              Close
            </button>
          </div>

          {/* Billing period toggle */}
          <div className="mx-auto mt-6 flex w-full max-w-sm items-center rounded-full border border-slate-200 bg-slate-100 p-1">
            <button type="button" className={seg(period === "monthly")} onClick={() => setPeriod("monthly")}>
              Monthly
            </button>
            <button type="button" className={seg(period === "annual")} onClick={() => setPeriod("annual")}>
              Annual
              <span className="whitespace-nowrap rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
                2 months free
              </span>
            </button>
          </div>
        </div>

        <div className="p-5 sm:p-7">
          {error && (
            <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
              {success}
            </div>
          )}

          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {[0, 1].map((i) => (
                <div key={i} className="h-80 animate-pulse rounded-2xl border border-slate-200 bg-slate-50" />
              ))}
              <p className="col-span-full text-center text-sm font-medium text-slate-600">
                Connecting securely to Stripe…
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {TIERS.map((tier) => {
                  const activeId = period === "annual" ? tier.annual.id : tier.monthly.id;
                  return (
                    <PlanCard
                      key={tier.key}
                      tier={tier}
                      period={period}
                      isCurrent={activePriceId === activeId}
                      isSubscribed={isSubscribed}
                      disabled={loading || (tier.chat && !chatPlansConfigured)}
                      onSelect={selectPlan}
                    />
                  );
                })}
              </div>

              <div className="mt-6 flex flex-col items-center gap-3 border-t border-slate-200 pt-5 text-center sm:flex-row sm:justify-between sm:text-left">
                <p className="text-sm text-slate-500">
                  Need to update your card or download invoices?
                </p>
                <button
                  className="secondary-button w-full sm:w-auto"
                  onClick={handleBillingPortal}
                  disabled={loading}
                  type="button"
                >
                  Open billing portal
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
